/**
 * Where a page may send the browser after a sign-in, when the place comes from the query string (`?callbackUrl=`).
 *
 * The login pages did `window.location.href = <the parameter>`: `javascript:...` ran script on the clinic's own origin once
 * the user had signed in, `//evil.example` and `https://evil.example` sent them away from a trusted address, and a browser
 * reads `/\evil.example` as `//evil.example`. What is let through is exactly what the app itself puts there:
 *
 *  - an absolute path of this site (`/reception`): one `/`, then no `/` or `\`, and no control character or space;
 *  - route-guard.ts sends people to the login with an ABSOLUTE url of the same origin (`https://<clinic>/reception`): so an
 *    http(s) url is let through when it is on `ctx.origin` (scheme, host and port), with no userinfo;
 *  - a SERVER caller that knows the platform's domain at run time (auth.ts: BASE_DOMAIN) may also pass `ctx.baseDomain`, and
 *    a url on that domain or below it is let through too (the Google flow lands on a clinic's subdomain). The PAGES do not:
 *    NEXT_PUBLIC_BASE_DOMAIN is fixed into the bundle when it is built, and a production image built without it would have
 *    them compare with the staging domain.
 *
 * Anything else becomes `fallback`. An absolute url comes back as the parsed one (`URL.href`); a path comes back as it
 * was given, it having passed the character test above (and a path cannot leave the origin: `/..//x` is still a path of
 * it). Pure, so the pages call it at the moment they navigate and a check can drive it directly.
 */
export interface RedirectContext {
    /** `window.location.origin` of the page, when there is one. */
    origin?: string;
    /** The platform's domain; a host equal to it or below it is the platform's. For callers that read it at run time. */
    baseDomain?: string;
}

export function safeCallbackUrl(value: string | null | undefined, fallback: string, ctx: RedirectContext = {}): string {
    if (!value) return fallback;
    if (/^\/(?![/\\])[^\x00-\x20\x7f]*$/.test(value)) return value;

    let url: URL;
    try { url = new URL(value); } catch { return fallback; }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return fallback;
    if (url.username || url.password) return fallback;

    const host = url.hostname.toLowerCase();
    const base = ctx.baseDomain?.toLowerCase();
    const onThisOrigin = ctx.origin !== undefined && url.origin === ctx.origin;
    const onThePlatform = !!base && (host === base || host.endsWith(`.${base}`));
    return onThisOrigin || onThePlatform ? url.href : fallback;
}
